import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Geolocation } from '@capacitor/geolocation'
import { MapPin, LogOut, Camera as CameraIcon } from 'lucide-react'
import { useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { apiUrl } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { useCheckInVisit, useCheckOutVisit, useUpdateVisit, useVisit, usePhotoTicket } from '@/features/field_visits/api'
import { createOrder, searchProducts } from '@/features/orders/api'

export function MobileVisit() {
  const { visitId } = useParams<{ visitId: string }>()
  const { data: visit, isLoading, error: fetchError } = useVisit(visitId!)
  const checkIn = useCheckInVisit()
  const checkOut = useCheckOutVisit()
  const updateVisit = useUpdateVisit()
  const photoTicket = usePhotoTicket()
  
  const [gpsError, setGpsError] = useState<string | null>(null)
  const [photoStatus, setPhotoStatus] = useState<string | null>(null)
  const [signatureStatus, setSignatureStatus] = useState<string | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  
  // Order state
  const [selectedProduct, setSelectedProduct] = useState<string>('')
  const [quantity, setQuantity] = useState<number>(1)
  const [orderStatus, setOrderStatus] = useState<string | null>(null)
  // One key for the life of this attempt -- a rep retrying "Create Order" after a
  // dropped connection in the field must replay onto the same order, not raise a second.
  const orderIdempotencyKeyRef = useRef(crypto.randomUUID())
  
  // Local state for editing before save
  const [notes, setNotes] = useState<string>('')
  const [outcome, setOutcome] = useState<string>('')
  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize local state from visit data
  if (visit && !isInitialized) {
    setNotes(visit.notes || '')
    setOutcome(visit.outcome || 'completed')
    setIsInitialized(true)
  }

  const handleCheckIn = async () => {
    setGpsError(null)
    try {
      const permission = await Geolocation.requestPermissions()
      if (permission.location === 'denied') {
        setGpsError('Location permission was refused.')
        return
      }
      const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true })
      await checkIn.mutateAsync({
        visitId: visitId!,
        fix: { lat: position.coords.latitude, lng: position.coords.longitude },
      })
    } catch (error) {
      setGpsError(error instanceof Error ? error.message : 'Could not get a GPS fix or check in.')
    }
  }

  const handleCheckOut = async () => {
    setGpsError(null)
    try {
      const permission = await Geolocation.requestPermissions()
      if (permission.location === 'denied') {
        setGpsError('Location permission was refused.')
        return
      }
      const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true })
      await checkOut.mutateAsync({
        visitId: visitId!,
        fix: { lat: position.coords.latitude, lng: position.coords.longitude },
      })
    } catch (error) {
      setGpsError(error instanceof Error ? error.message : 'Could not get a GPS fix or check out.')
    }
  }

  const handleSaveDetails = async () => {
    try {
      await updateVisit.mutateAsync({
        visitId: visitId!,
        data: { notes, outcome },
      })
    } catch {
      // Error handled by the mutation or caught here
    }
  }

  const handleTakePhoto = async () => {
    try {
      setPhotoStatus('Starting camera...')
      const image = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
      })

      if (!image.webPath) {
        setPhotoStatus('Error: No image returned from camera.')
        return
      }

      setPhotoStatus('Getting upload ticket...')
      const ticket = await photoTicket.mutateAsync({ visitId: visitId! })

      setPhotoStatus('Uploading photo...')
      const response = await fetch(image.webPath)
      const blob = await response.blob()

      const uploadRes = await fetch(apiUrl(ticket.url), {
        method: ticket.method,
        headers: {
          'Content-Type': ticket.content_type,
        },
        body: blob,
      })

      if (!uploadRes.ok) {
        setPhotoStatus('Error uploading photo.')
        return
      }

      setPhotoStatus('Saving photo to visit...')
      await updateVisit.mutateAsync({
        visitId: visitId!,
        data: { photo_key: ticket.key },
      })
      
      setPhotoStatus('Photo attached successfully.')
    } catch (error) {
      setPhotoStatus(`Camera/Upload error: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  // Signature drawing logic
  const startDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    
    setIsDrawing(true)
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    ctx.beginPath()
    ctx.moveTo(x, y)
    
    // Support touch devices (prevent scrolling while signing)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const stopDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    setIsDrawing(false)
    e.currentTarget.releasePointerCapture(e.pointerId)
  }

  const clearSignature = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  const handleSaveSignature = async () => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    // Check if empty (simplified check)
    setSignatureStatus('Saving signature...')
    try {
      const dataUrl = canvas.toDataURL('image/png')
      await updateVisit.mutateAsync({
        visitId: visitId!,
        data: { signature_data: dataUrl },
      })
      setSignatureStatus('Signature attached successfully.')
    } catch (error) {
      setSignatureStatus(`Error saving signature: ${error instanceof Error ? error.message : 'unknown error'}`)
    }
  }

  const { data: productsData } = useQuery({
    queryKey: ['products_options'],
    queryFn: () => searchProducts(),
  })
  const products = productsData?.data || []

  const handleCreateOrder = async () => {
    if (!selectedProduct || quantity <= 0) return
    setOrderStatus('Creating order...')
    try {
      // 1. Create the order
      const order = await createOrder(
        {
          customer_id: visit!.customer_id,
          items: [{ product_id: selectedProduct, quantity }],
        },
        orderIdempotencyKeyRef.current,
      )
      
      // 2. Patch the visit with order_id
      await updateVisit.mutateAsync({
        visitId: visitId!,
        data: { order_id: order.id },
      })
      setOrderStatus('Order created and linked successfully.')
    } catch (error) {
      setOrderStatus(`Error creating order: ${error instanceof Error ? error.message : 'unknown error'}`)
    }
  }

  if (isLoading) return <div>Loading visit...</div>
  if (fetchError || !visit) return <Alert tone="error">Could not load visit.</Alert>

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader 
          title={`Visit ${visit.visit_number}`} 
          description={`Status: ${visit.status}`} 
        />
        <CardBody className="space-y-4">
          
          {(gpsError || checkIn.error || checkOut.error || updateVisit.error || photoTicket.error || photoStatus?.includes('Error')) && (
            <Alert tone="error">
              {gpsError || checkIn.error?.message || checkOut.error?.message || updateVisit.error?.message || photoTicket.error?.message || photoStatus}
            </Alert>
          )}

          {visit.status === 'planned' && (
            <Button className="h-11 w-full" onClick={handleCheckIn} loading={checkIn.isPending}>
              <MapPin className="h-4 w-4" aria-hidden="true" />
              Check In
            </Button>
          )}

          {visit.status === 'in_progress' && (
            <div className="space-y-4">
              <div className="rounded-md bg-slate-50 p-3 text-sm dark:bg-slate-800/60">
                <p className="text-slate-700 dark:text-slate-300">
                  Checked in at: {new Date(visit.checked_in_at!).toLocaleTimeString()}
                </p>
                {visit.check_in_within_fence === false && (
                  <p className="font-medium text-amber-600 dark:text-amber-400">
                    Flagged: Check-in was outside the {visit.geofence_radius_m}m geofence.
                  </p>
                )}
              </div>

              <div className="space-y-3 rounded-md border border-slate-200 p-4 dark:border-slate-700">
                <h3 className="font-semibold text-slate-800 dark:text-slate-200">Photo Proof</h3>
                {visit.photo_key ? (
                  <div className="flex items-center text-sm font-medium text-green-600 dark:text-green-400">
                    Photo attached.
                  </div>
                ) : (
                  <>
                    <Button 
                      variant="secondary" 
                      className="w-full" 
                      onClick={handleTakePhoto} 
                      disabled={photoTicket.isPending}
                    >
                      <CameraIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                      Take Photo
                    </Button>
                    {photoStatus && !photoStatus.includes('Error') && (
                      <p className="text-xs text-slate-500">{photoStatus}</p>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-3 rounded-md border border-slate-200 p-4 dark:border-slate-700">
                <h3 className="font-semibold text-slate-800 dark:text-slate-200">Customer Signature</h3>
                {visit.signature_data ? (
                  <div className="space-y-2">
                    <div className="flex items-center text-sm font-medium text-green-600 dark:text-green-400">
                      Signature attached.
                    </div>
                    <img 
                      src={visit.signature_data} 
                      alt="Customer Signature" 
                      className="rounded-md border border-slate-200 bg-white" 
                      width="100%"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <canvas
                      ref={canvasRef}
                      width={300}
                      height={150}
                      onPointerDown={startDrawing}
                      onPointerMove={draw}
                      onPointerUp={stopDrawing}
                      onPointerCancel={stopDrawing}
                      className="w-full touch-none rounded-md border border-slate-300 bg-white dark:border-slate-600"
                      style={{ touchAction: 'none' }}
                    />
                    <div className="flex gap-2">
                      <Button variant="secondary" className="flex-1" onClick={clearSignature}>
                        Clear
                      </Button>
                      <Button className="flex-1" onClick={handleSaveSignature} loading={updateVisit.isPending}>
                        Save Signature
                      </Button>
                    </div>
                    {signatureStatus && (
                      <p className={`text-xs ${signatureStatus.includes('Error') ? 'text-red-500' : 'text-slate-500'}`}>
                        {signatureStatus}
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="space-y-3 rounded-md border border-slate-200 p-4 dark:border-slate-700">
                <h3 className="font-semibold text-slate-800 dark:text-slate-200">Capture Order</h3>
                {visit.order_id ? (
                  <div className="flex items-center text-sm font-medium text-green-600 dark:text-green-400">
                    Order linked to this visit.
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Product</label>
                      <select
                        value={selectedProduct}
                        onChange={(e) => setSelectedProduct(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-700 dark:text-white"
                      >
                        <option value="">Select a product...</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} ({p.code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Quantity</label>
                      <input
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={(e) => setQuantity(Number(e.target.value))}
                        className="flex h-10 w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-700 dark:text-white"
                      />
                    </div>
                    <Button 
                      className="w-full" 
                      onClick={handleCreateOrder}
                      disabled={!selectedProduct || quantity <= 0}
                    >
                      Create Order
                    </Button>
                    {orderStatus && (
                      <p className={`text-xs ${orderStatus.includes('Error') ? 'text-red-500' : 'text-slate-500'}`}>
                        {orderStatus}
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="space-y-3 rounded-md border border-slate-200 p-4 dark:border-slate-700">
                <h3 className="font-semibold text-slate-800 dark:text-slate-200">Visit Details</h3>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Outcome</label>
                  <select 
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-50 dark:focus:ring-slate-400 dark:focus:ring-offset-slate-900"
                  >
                    <option value="completed">Completed</option>
                    <option value="no_answer">No Answer</option>
                    <option value="rescheduled">Rescheduled</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Notes</label>
                  <textarea 
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="flex min-h-[80px] w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-50 dark:focus:ring-slate-400 dark:focus:ring-offset-slate-900"
                    placeholder="Enter visit notes..."
                  />
                </div>
                
                <Button 
                  className="w-full" 
                  onClick={handleSaveDetails} 
                  loading={updateVisit.isPending}
                >
                  Save Details
                </Button>
              </div>
              
              <Button 
                variant="secondary" 
                className="h-11 w-full" 
                onClick={handleCheckOut} 
                loading={checkOut.isPending}
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Check Out
              </Button>
            </div>
          )}

          {visit.status === 'completed' && (
            <div className="rounded-md bg-green-50 p-3 text-sm dark:bg-green-900/20">
              <p className="font-medium text-green-800 dark:text-green-300">
                Visit Completed
              </p>
              <p className="text-green-700 dark:text-green-400">
                Checked out at: {new Date(visit.checked_out_at!).toLocaleTimeString()}
              </p>
            </div>
          )}

        </CardBody>
      </Card>
    </div>
  )
}
